import { AdminModuleKey, ModulePermissionLevel, ModulePermissions, SubModuleKey, SubModulePermissions, User } from '../types';

export type AccessSubPermissionGroup = 'visibility' | 'action' | 'scope' | 'legacy';

export type SystemAccessTemplateKey =
  | 'supervisor_minimum'
  | 'field_supervisor'
  | 'timesheet_supervisor'
  | 'field_reports_supervisor'
  | 'schedule_viewer'
  | 'lead_foreman'
  | 'project_manager'
  | 'office_payroll'
  | 'executive'
  | 'read_only'
  | 'full_admin';
export type AccessTemplateKey = SystemAccessTemplateKey | string;

export const ADMIN_MODULES: { key: AdminModuleKey; label: string; description: string }[] = [
  { key: 'dashboard', label: 'Dashboard', description: 'KPIs, admin queue, operational overview.' },
  { key: 'timesheets', label: 'Timesheets', description: 'View, edit, approve, reject, and reopen timesheets.' },
  { key: 'projects', label: 'Projects', description: 'Projects, budgets, staffing, and change orders.' },
  { key: 'schedule', label: 'Schedule', description: 'Gantt planning, tasks, categories, and dependencies.' },
  { key: 'field_logs', label: 'Field Reports', description: 'Daily field reports, photos, notes, and submitted field activity.' },
  { key: 'control', label: 'Control', description: 'Plan Principal, Plan Actual, Variaciones and Snapshots.' },
  { key: 'people', label: 'People', description: 'Employees, supervisors, admins, and requests.' },
  { key: 'reports', label: 'Reports', description: 'Production overview, installation tracking, and progress. Field Reports and Control are managed via their own modules below.' },
  { key: 'employee_hours', label: 'Employee Hours', description: 'Hours reports, OT, sick days, and labor cost breakdowns.' },
  { key: 'prior_period', label: 'Prior Period', description: 'Historical project cost, hours, and installed quantities before Forxa tracking.' },
  { key: 'export', label: 'Exports', description: 'Excel exports and downloadable files.' },
  { key: 'settings', label: 'Settings', description: 'Company settings and configuration pages.' },
  { key: 'security', label: 'Security', description: 'Operator security center and sensitive audit views.' },
  { key: 'in_progress', label: 'In Progress', description: 'Developer-controlled preview area for modules that are being built but are not published yet.' },
  { key: 'requests', label: 'Requests', description: 'Project requests, preparation, delivery and receipt confirmation.' },
  { key: 'warehouse', label: 'Warehouse', description: 'Inventory, incoming deliveries, movements, purchases, receipts and vendors.' },
  { key: 'documents', label: 'Documents', description: 'Project documents, controlled categories, and version history.' },
  { key: 'drawings', label: 'Drawings', description: 'Drawing sets, revisions, publishing, and annotations.' },
  { key: 'rfis', label: 'RFIs', description: 'Internal requests for information, responses, deadlines, and history.' },
  { key: 'free_reports', label: 'Free Reports', description: 'Free-form daily logs per project with people, notes, photos, and PDFs. A record only: never counts toward progress or costs.' },
  { key: 'estimates', label: 'Bids / Estimates', description: 'Preconstruction bids and estimate files.' },
];

export const FULL_MODULE_PERMISSIONS: ModulePermissions = {
  dashboard: 'manage',
  timesheets: 'manage',
  projects: 'manage',
  schedule: 'manage',
  field_logs: 'manage',
  control: 'manage',
  people: 'manage',
  reports: 'manage',
  employee_hours: 'manage',
  prior_period: 'manage',
  export: 'manage',
  settings: 'manage',
  security: 'none', // Developer-controlled — off by default
  in_progress: 'none', // Developer-controlled — off by default
  warehouse: 'none',
  requests: 'none',
  documents: 'manage',
  drawings: 'manage',
  rfis: 'manage',
  free_reports: 'manage',
  estimates: 'none',
};

/** Default for a newly created admin — no access until the Operator grants it. */
export const DEFAULT_ADMIN_PERMISSIONS: ModulePermissions = {
  dashboard: 'none',
  timesheets: 'none',
  projects: 'none',
  schedule: 'none',
  field_logs: 'none',
  control: 'none',
  people: 'none',
  reports: 'none',
  employee_hours: 'none',
  prior_period: 'none',
  export: 'none',
  settings: 'none',
  security: 'none',
  in_progress: 'none',
  warehouse: 'none',
  requests: 'none',
  documents: 'none',
  drawings: 'none',
  rfis: 'none',
  free_reports: 'none',
  estimates: 'none',
};

/** Default permissions for a newly-created supervisor — minimal by design.
 *  Operators can expand or restrict these per-supervisor as needed.
 *
 *  Supervisors can submit timesheets, see assigned projects, and view assigned-project hour summaries.
 *  Schedule, Field Reports, Reports, and other modules must be explicitly granted by template or module access. */
export const DEFAULT_SUPERVISOR_PERMISSIONS: ModulePermissions = {
  dashboard: 'view',
  timesheets: 'manage',
  projects: 'view',
  schedule: 'none',
  field_logs: 'none',
  control: 'none',
  people: 'none',
  reports: 'none',
  employee_hours: 'view',
  prior_period: 'none',
  export: 'none',
  settings: 'none',
  security: 'none',
  in_progress: 'none',
  warehouse: 'none',
  requests: 'none',
  documents: 'none',
  drawings: 'none',
  rfis: 'none',
  free_reports: 'none',
  estimates: 'none',
};

const MODULE_LEVEL_RANK: Record<ModulePermissionLevel, number> = { none: 0, view: 1, manage: 2 };

export const SUPERVISOR_MODULE_CAPS: ModulePermissions = {
  dashboard: 'view',
  timesheets: 'manage',
  projects: 'view',
  schedule: 'manage',
  field_logs: 'manage',
  control: 'none',
  people: 'none',
  reports: 'view',
  employee_hours: 'view',
  prior_period: 'none',
  export: 'none',
  settings: 'none',
  security: 'none',
  in_progress: 'none',
  warehouse: 'view',
  requests: 'view',
  // manage only so a foreman can be allowed to upload (documents.upload,
  // limited per folder); versions, metadata, archive and folders stay admin.
  documents: 'manage',
  drawings: 'view',
  rfis: 'view',
  free_reports: 'manage',
  estimates: 'none',
};

export const SUPERVISOR_MONEY_SUB_PERMISSIONS: SubModuleKey[] = [
  'dashboard.project_health',
  'dashboard.labor',
  'employee_hours.show_rates',
  'employee_hours.show_admin_rates',
  'employee_hours.show_earnings',
  'employee_hours.view_admin_hours',
  'timesheets.personal.submit',
  'timesheets.personal.approve',
  'timesheets.personal.manage_others',
  'employee_hours.delete_rate_history',
  'employee_hours.edit_rate_history',
  'reports.tab_workforce',
  'projects.financials.view',
  'projects.financials.manage',
  'prior_period.baseline.edit',
  'employee_hours.edit_rate',
  'employee_hours.view_sick_pay',
  'employee_hours.view_holiday_pay',
  'employee_hours.manage_holiday_pay',
  'employee_hours.holiday_pay.edit',
  'employee_hours.holiday_pay.approve',
  'employee_hours.special_pay.manage',
  'employee_hours.payroll.close',
  'projects.manage_financials',
  'warehouse.can_edit_lot_cost',
  'warehouse.purchases_view',
  'warehouse.purchases_create_draft',
  'warehouse.purchases_upload_receipt',
  'warehouse.purchases_submit',
  'warehouse.purchases_link_request',
  'warehouse.purchases_post',
  'warehouse.purchases_void',
  'warehouse.purchases_reopen_posted',
  'warehouse.purchases_approve_receipt_exception',
  'warehouse.purchases_edit_draft',
  'warehouse.purchases_delete_draft',
  'warehouse.purchases_recall',
  'warehouse.purchases_return_for_correction',
];

export const SUPERVISOR_ALLOWED_SUB_PERMISSIONS: SubModuleKey[] = [
  'projects.progress.view',
  'requests.notifications',
  'projects.workforce.notifications',
  // Per-person maintenance exception, granted only by the Developer on the server.
  'security.bypass_maintenance',
  'employee_hours.own_project_only',
  'employee_hours.schedule_pto',
  'reports.overview_general',
  'reports.tab_production',
  'field_logs.can_view',
  'field_logs.can_submit',
  'field_logs.view_all',
  'field_logs.create',
  'field_logs.edit',
  'field_logs.delete',
  'field_logs.attachments',
  'free_reports.create',
  'free_reports.edit',
  'free_reports.delete',
  'free_reports.attachments',
  'free_reports.can_submit',
  'free_reports.view_all',
  'documents.upload',
  'projects.workforce.photo',
  'projects.workforce.emergency_contacts',
  'projects.workforce.credentials',
  'projects.workforce.people',
  'projects.workforce.add_existing',
  'projects.tasks.view',
  'projects.tasks.no_work_day',
  'schedule.update_progress',
  'production.zone_soft_restrict',
  'warehouse.analytics_view',
  'warehouse.can_checkout',
  'warehouse.can_checkin',
  'warehouse.worker_tools_issue',
  'warehouse.can_transfer',
  'warehouse.can_report_condition',
  'warehouse.can_manage_catalog',
  'warehouse.can_manage_locations',
  'warehouse.requests_view_own',
  'warehouse.requests_view_project',
  'warehouse.requests_create',
  'warehouse.requests_cancel_own',
  'warehouse.requests_manage_queue',
  'warehouse.requests_prepare',
  'warehouse.requests_dispatch',
  'warehouse.requests_record_delivery',
  'warehouse.requests_close_not_supplied',
  'warehouse.requests_confirm_receipt',
  'warehouse.requests_dispute_receipt',
  'warehouse.requests_manual_asset_override',
  'warehouse.requests_view_history',
  'warehouse.requests_comment',
  'warehouse.inventory_corrections_create',
  'warehouse.inventory_corrections_review',
  'warehouse.inventory_corrections_apply',
  'warehouse.inventory_corrections_reverse',
  'timesheets.records.create',
  'timesheets.records.edit',
  'timesheets.records.submit',
  'timesheets.records.delete_row',
  'timesheets.records.any_worker',
  'timesheets.records.view_others',
  'timesheets.records.special_hours',
  'timesheets.can_delete',
  'timesheets.tickets.create',
  'timesheets.tickets.view_all',
  'timesheets.tickets.prepare_documents',
  'timesheets.tickets.request_signature_links',
  'timesheets.tickets.upload_files',
  'timesheets.tickets.submit',
  'timesheets.tickets.delete',
];

export const SUB_MODULES: Array<{
  key: SubModuleKey;
  moduleKey: AdminModuleKey;
  label: string;
  description: string;
  group: AccessSubPermissionGroup;
}> = [
  { key: 'estimates.view_all', moduleKey: 'estimates', label: 'View other bids', description: 'View bids owned by other estimators.', group: 'scope' },
  { key: 'estimates.view_amounts', moduleKey: 'estimates', label: 'View other estimate amounts', description: 'Read amounts and estimate files belonging to other estimators.', group: 'visibility' },
  { key: 'estimates.mark_won', moduleKey: 'estimates', label: 'Mark won', description: 'Convert a bid and transfer files with project permissions.', group: 'action' },
  { key: 'estimates.delete', moduleKey: 'estimates', label: 'Archive bids', description: 'Archive bids without destroying history.', group: 'action' },
  { key: 'requests.notifications', moduleKey: 'requests', label: 'Requests: Receive notifications', description: 'Receive request updates within authorized projects. Existing visibility rules still apply.', group: 'visibility' },
  { key: 'people.notifications', moduleKey: 'people', label: 'People: Receive HR reminders', description: 'Receive personnel reminders and assignment changes already visible to this account.', group: 'visibility' },
  { key: 'projects.workforce.notifications', moduleKey: 'projects', label: 'People: Receive personnel movement notices', description: 'Receive personnel movement notices within assigned projects, without private fields or pay data.', group: 'visibility' },
  {
    key: 'dashboard.project_health',
    moduleKey: 'dashboard',
    label: 'Dashboard: Project Health',
    description: 'Shows the executive project health section with budget, schedule, and install progress.',
    group: 'visibility',
  },
  {
    key: 'dashboard.labor',
    moduleKey: 'dashboard',
    label: 'Dashboard: Labor',
    description: 'Shows dashboard labor, timesheet, overtime, and approval signals.',
    group: 'visibility',
  },
  {
    key: 'dashboard.schedule',
    moduleKey: 'dashboard',
    label: 'Dashboard: Schedule',
    description: 'Shows dashboard schedule risk, forecast, dependencies, and recovery signals.',
    group: 'visibility',
  },
  {
    key: 'dashboard.reports',
    moduleKey: 'dashboard',
    label: 'Dashboard: Reports',
    description: 'Shows dashboard report coverage, installed quantities, and field progress gaps.',
    group: 'visibility',
  },
  {
    key: 'dashboard.alerts',
    moduleKey: 'dashboard',
    label: 'Dashboard: Alerts',
    description: 'Shows dashboard operational alerts and attention items.',
    group: 'visibility',
  },
  {
    key: 'dashboard.credentials',
    moduleKey: 'dashboard',
    label: 'Dashboard: Credentials',
    description: 'Shows expired and expiring employee credentials on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.offboarding',
    moduleKey: 'dashboard',
    label: 'Dashboard: Offboarding',
    description: 'Shows employees with a scheduled offboarding on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.rfi_attention',
    moduleKey: 'dashboard',
    label: 'Dashboard: RFI Attention',
    description: 'Shows overdue and soon-due open RFIs on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.warehouse_attention',
    moduleKey: 'dashboard',
    label: 'Dashboard: Warehouse Attention',
    description: 'Shows late deliveries and open material requests on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.records_attention',
    moduleKey: 'dashboard',
    label: 'Dashboard: Incomplete Records',
    description: 'Shows employees missing an emergency contact or a rate on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.projects_at_risk',
    moduleKey: 'dashboard',
    label: 'Dashboard: Projects at Risk',
    description: 'Shows projects flagged at risk by budget or schedule on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.schedule_exceptions',
    moduleKey: 'dashboard',
    label: 'Dashboard: Schedule Exceptions',
    description: 'Shows projects with blocked or delayed tasks on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'dashboard.production_vs_plan',
    moduleKey: 'dashboard',
    label: 'Dashboard: Production vs Plan',
    description: 'Shows projects where installed progress lags budget spent or has weak field-progress coverage.',
    group: 'visibility',
  },
  {
    key: 'dashboard.payroll_exceptions',
    moduleKey: 'dashboard',
    label: 'Dashboard: Payroll Exceptions',
    description: 'Shows rejected or reopened timesheets still unresolved on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'employee_hours.show_rates',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Show hourly rates',
    description: 'Allows this user to see employee hourly rates.',
    group: 'action',
  },
  {
    key: 'employee_hours.show_earnings',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Show earnings and labor cost',
    description: 'Allows this user to see total pay, labor cost, and money columns.',
    group: 'action',
  },
  {
    key: 'employee_hours.own_project_only',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Assigned projects only',
    description: 'Restricts the view to employees connected to assigned projects.',
    group: 'scope',
  },
  {
    key: 'reports.overview_general',
    moduleKey: 'reports',
    label: 'General overview',
    description: 'Shows the general production overview and executive report summary.',
    group: 'visibility',
  },
  {
    key: 'reports.tab_production',
    moduleKey: 'reports',
    label: 'Production tab',
    description: 'Shows production quantities, install progress, issues, and logs.',
    group: 'visibility',
  },
  {
    key: 'reports.tab_workforce',
    moduleKey: 'reports',
    label: 'Workforce tab',
    description: 'Shows workforce plan, crew assumptions, and manpower analysis.',
    group: 'visibility',
  },
  {
    key: 'reports.tab_timeline',
    moduleKey: 'reports',
    label: 'Timeline / comparison tab',
    description: 'Shows report comparison and timeline-style progress views.',
    group: 'visibility',
  },
  {
    key: 'reports.setup_structure',
    moduleKey: 'reports',
    label: 'Setup: Structure',
    description: 'Allows access to the report structure builder.',
    group: 'action',
  },
  {
    key: 'reports.setup_targets',
    moduleKey: 'reports',
    label: 'Setup: Targets',
    description: 'Allows access to target quantities, rates, crew, and duration setup.',
    group: 'action',
  },
  {
    key: 'reports.can_submit',
    moduleKey: 'reports',
    label: 'Edit production entries',
    description: 'Allows logging installation quantities from the production overview. Field report submission is controlled under Field Reports.',
    group: 'action',
  },
  {
    key: 'people.employees',
    moduleKey: 'people',
    label: 'Employees',
    description: 'Shows the employee directory and employee profile controls.',
    group: 'visibility',
  },
  {
    key: 'people.employee_requests',
    moduleKey: 'people',
    label: 'Employee requests',
    description: 'Shows pending employee requests submitted from timesheets.',
    group: 'visibility',
  },
  {
    key: 'people.supervisors',
    moduleKey: 'people',
    label: 'Supervisors',
    description: 'Shows supervisor accounts and supervisor assignment access.',
    group: 'visibility',
  },
  {
    key: 'people.admins',
    moduleKey: 'people',
    label: 'Admins',
    description: 'Shows admin account management for operator users.',
    group: 'action',
  },
  {
    key: 'people.manage_module_access',
    moduleKey: 'people',
    label: 'Module Access Management',
    description: 'Allows this admin to configure module access permissions for supervisors, up to their own access level.',
    group: 'action',
  },
  {
    key: 'people.rented',
    moduleKey: 'people',
    label: 'Rented People',
    description: 'Shows the Rented People tab with project-level rented workers and crew details.',
    group: 'visibility',
  },
  {
    key: 'people.credentials',
    moduleKey: 'people',
    label: 'Credentials',
    description: 'Shows the company-wide Credentials tab tracking certifications, licenses, and their expiration status.',
    group: 'visibility',
  },
  {
    key: 'people.documents',
    moduleKey: 'people',
    label: 'Employee Documents',
    description: 'View and manage on-file HR paperwork per employee (W-4, I-9, direct deposit, etc.). Separate from Credentials since it is more sensitive personal/tax data.',
    group: 'visibility',
  },
  {
    key: 'people.assignments.manage',
    moduleKey: 'people',
    label: 'People: Manage dated assignments',
    description: 'Assign and move workers with effective dates. Destination must be within your project access; external lookup reveals only name and origin project.',
    group: 'action',
  },
  {
    key: 'people.mode.manage',
    moduleKey: 'people',
    label: 'People: Change company mode',
    description: 'Switch the whole company between Current and Interactive People.',
    group: 'action',
  },
  {
    key: 'people.manage_credential_types',
    moduleKey: 'people',
    label: 'Manage Credential Types',
    description: 'Allows creating, editing, and deleting the company-wide catalog of credential types (e.g. OSHA 30, driver license).',
    group: 'action',
  },
  { key: 'field_logs.view_all', moduleKey: 'field_logs', label: 'Field Reports: See reports by other supervisors', description: 'Read reports from other supervisors within authorized projects. Without this permission, only your own reports are visible.', group: 'visibility' },
  { key: 'field_logs.create', moduleKey: 'field_logs', label: 'Field Reports: Create reports', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'field_logs.edit', moduleKey: 'field_logs', label: 'Field Reports: Edit reports and entries', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'field_logs.delete', moduleKey: 'field_logs', label: 'Field Reports: Delete reports', description: 'Delete a report. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'field_logs.attachments', moduleKey: 'field_logs', label: 'Field Reports: Manage attachments and notes', description: 'Upload and remove report files and their annotations. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'free_reports.create', moduleKey: 'free_reports', label: 'Free Reports: Create reports', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'free_reports.edit', moduleKey: 'free_reports', label: 'Free Reports: Edit reports and entries', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'free_reports.delete', moduleKey: 'free_reports', label: 'Free Reports: Delete reports', description: 'Delete a report. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'free_reports.attachments', moduleKey: 'free_reports', label: 'Free Reports: Manage attachments and notes', description: 'Upload and remove report files and their annotations. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'field_logs.approve', moduleKey: 'field_logs', label: 'Field Reports: Approve reports', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'field_logs.reopen', moduleKey: 'field_logs', label: 'Field Reports: Return reports to draft', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  { key: 'free_reports.can_submit', moduleKey: 'free_reports', label: 'Free Reports: Submit reports', description: 'Allows this action separately from the other report permissions. Existing report ownership and locks still apply.', group: 'action' },
  {
    key: 'field_logs.can_view',
    moduleKey: 'field_logs',
    label: 'View field reports',
    description: 'Allows this user to see the Field Reports tab and read submitted reports. Enabled by default for admins and supervisors.',
    group: 'legacy',
  },
  {
    key: 'field_logs.can_submit',
    moduleKey: 'field_logs',
    label: 'Field Reports: Submit reports',
    description: 'Allows this user to submit (finalize) field reports. Admins can always submit; supervisors need this enabled explicitly.',
    group: 'action',
  },
  {
    key: 'control.can_snapshot',
    moduleKey: 'control',
    label: 'Create snapshots',
    description: 'Allows creating control snapshots and variation records.',
    group: 'action',
  },
  {
    key: 'control.can_lock_baseline',
    moduleKey: 'control',
    label: 'Lock baseline',
    description: 'Allows setting or replacing the project baseline.',
    group: 'action',
  },
  {
    key: 'security.bypass_maintenance',
    moduleKey: 'security',
    label: 'Bypass maintenance mode',
    description: 'Allows this user to access the app normally even when manual maintenance mode is active. Only the developer can grant this.',
    group: 'action',
  },
  {
    key: 'timesheets.edit_pending',
    moduleKey: 'timesheets',
    label: 'Edit pending timesheets',
    description: 'Allows an admin to correct a submitted timesheet without rejecting it or changing its Pending status.',
    group: 'action',
  },
  {
    key: 'timesheets.can_reopen',
    moduleKey: 'timesheets',
    label: 'Reopen approved timesheets',
    description: 'Allows this user to reopen already approved timesheets (payroll impact).',
    group: 'action',
  },
  {
    key: 'timesheets.records.view_others', moduleKey: 'timesheets', label: 'Timesheets: View others',
    description: 'View approved timesheets from other users within accessible projects. No drafts, pending, rejected or reopened records. Review, editing and financial access require separate permissions.', group: 'visibility',
  },
  { key: 'timesheets.personal.submit', moduleKey: 'timesheets', label: 'My hours / Mis horas', description: 'Fill and submit your personal weekly sheet. Enabled individually for admins.', group: 'visibility' },
  { key: 'timesheets.personal.approve', moduleKey: 'timesheets', label: 'Approve personal hours / Aprobar horas personales', description: 'Accountant review. Requires admin-hours visibility. Never approve your own sheet.', group: 'visibility' },
  { key: 'timesheets.personal.manage_others', moduleKey: 'timesheets', label: 'Fill hours for other admins / Llenar horas de otros admins', description: 'Fill, submit or withdraw the personal weekly sheet of another admin. Whoever fills a sheet cannot approve it. Requires admin-hours visibility.', group: 'action' },
  { key: 'employee_hours.show_admin_rates', moduleKey: 'employee_hours', label: 'View admin rates and salaries / Ver tarifas y salarios de admins', description: 'Admin compensation only. Personal hours also require View admin hours.', group: 'action' },
  { key: 'employee_hours.view_admin_hours', moduleKey: 'employee_hours', label: 'View admin hours / Ver horas de admins', description: 'Include personal hours in reports, Excel and project costs.', group: 'visibility' },
  {
    key: 'timesheets.records.view_drafts', moduleKey: 'timesheets', label: 'Timesheets: View others\' drafts',
    description: 'Admins only. View draft timesheets from other users within accessible projects, read-only: they cannot be edited, submitted or deleted by this user. Amounts still require earnings visibility.', group: 'visibility',
  },
  {
    key: 'timesheets.records.create',
    moduleKey: 'timesheets',
    label: 'Timesheets: Create',
    description: 'Allows creating a new timesheet.',
    group: 'action',
  },
  {
    key: 'timesheets.records.edit',
    moduleKey: 'timesheets',
    label: 'Timesheets: Edit',
    description: 'Allows editing a draft, rejected, or reopened timesheet.',
    group: 'action',
  },
  {
    key: 'timesheets.records.special_hours', moduleKey: 'timesheets', label: 'Timesheets: Special Hours',
    description: 'Register or change Sick Day and Training in weekly and daily timesheets. Without this permission, existing special hours stay read-only.', group: 'action',
  },
  {
    key: 'timesheets.records.submit',
    moduleKey: 'timesheets',
    label: 'Timesheets: Submit',
    description: 'Allows submitting a timesheet for approval.',
    group: 'action',
  },
  {
    key: 'timesheets.records.approve',
    moduleKey: 'timesheets',
    label: 'Timesheets: Approve',
    description: 'Allows approving a submitted timesheet.',
    group: 'action',
  },
  {
    key: 'timesheets.records.reject',
    moduleKey: 'timesheets',
    label: 'Timesheets: Reject',
    description: 'Allows rejecting a submitted timesheet.',
    group: 'action',
  },
  {
    key: 'timesheets.records.delete_row',
    moduleKey: 'timesheets',
    label: 'Timesheets: Delete a row',
    description: 'Allows deleting a single row from a timesheet.',
    group: 'action',
  },
  {
    key: 'timesheets.records.any_worker',
    moduleKey: 'timesheets',
    label: 'Timesheets: Add any worker',
    description: 'Interactive People: add a worker who is not on the project to one timesheet week (for example a past week). It enables the person only on that project for that week and does not change their project in People.',
    group: 'action',
  },
  {
    key: 'timesheets.can_delete',
    moduleKey: 'timesheets',
    label: 'Timesheets: Delete entire timesheet',
    description: 'Allows permanently deleting an entire timesheet, including already-approved ones. Separate from Reopen — this is the more destructive action.',
    group: 'action',
  },
  {
    key: 'timesheets.tickets.view_all', moduleKey: 'timesheets', label: 'Tickets: Access all assigned-project tickets',
    description: 'Read tickets created by other foremen, only in assigned projects. Editing, attachments and signature requests remain limited to your own tickets.', group: 'scope',
  },
  {
    key: 'timesheets.tickets.prepare_documents', moduleKey: 'timesheets', label: 'Tickets: Prepare PDF sheets',
    description: 'Prepare operational PDF sheets. Does not authorize collecting or requesting signatures.', group: 'action',
  },
  {
    key: 'timesheets.tickets.request_signature_links', moduleKey: 'timesheets', label: 'Tickets: Send signature links',
    description: 'Send private customer signature links. Disabled by default for foremen.', group: 'action',
  },
  {
    key: 'timesheets.tickets.authorize_signatures', moduleKey: 'timesheets', label: 'Tickets: Authorize in-person signatures',
    description: 'Admin only: authorize a named foreman to collect a signature on one frozen sheet version.', group: 'action',
  },
  {
    key: 'timesheets.tickets.create',
    moduleKey: 'timesheets',
    label: 'Tickets: Create',
    description: 'Allows creating tickets. Without Review, new tickets start as drafts that must be submitted.',
    group: 'action',
  },
  {
    key: 'timesheets.tickets.upload_files',
    moduleKey: 'timesheets',
    label: 'Tickets: Upload files',
    description: 'Allows attaching the signed ticket, photos, or backup files to a ticket.',
    group: 'action',
  },
  {
    key: 'timesheets.tickets.submit',
    moduleKey: 'timesheets',
    label: 'Tickets: Submit',
    description: 'Allows sending a draft ticket to an admin for review.',
    group: 'action',
  },
  {
    key: 'timesheets.tickets.review',
    moduleKey: 'timesheets',
    label: 'Tickets: Review and finish',
    description: 'Allows accepting or returning submitted tickets and managing accepted tickets. Ticket hours count toward the budget only after acceptance.',
    group: 'action',
  },
  {
    key: 'timesheets.tickets.delete', moduleKey: 'timesheets', label: 'Delete tickets',
    description: 'Hide tickets with a required reason within the allowed project and ticket scope. Only a developer can restore or permanently delete them. Off by default for every role.', group: 'action',
  },
  { key: 'projects.progress.view', moduleKey: 'projects', label: 'Projects: View progress',
    description: 'See accumulated production percentages, progress bars and progress comparisons. Independent from entering and submitting field reports.', group: 'visibility' },
  // projects.manage_financials remains in stored-permission compatibility only.
  {
    key: 'projects.financials.view',
    moduleKey: 'projects',
    label: 'View project financials',
    description: 'Allows viewing project budgets, crew rates, overhead, change orders, and external labor costs.',
    group: 'visibility',
  },
  {
    key: 'projects.financials.manage',
    moduleKey: 'projects',
    label: 'Manage project financials',
    description: 'Allows changing project budgets, crew rates, overhead, change orders, and external labor costs.',
    group: 'action',
  },
  {
    key: 'prior_period.baseline.edit',
    moduleKey: 'prior_period',
    label: 'Edit historical baseline',
    description: 'Allows changing historical project cost, hours, and installed quantities.',
    group: 'action',
  },
  {
    key: 'employee_hours.view_sick_pay',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: View Sick Pay',
    description: 'Allows this user to see Sick eligibility decisions and paid Sick hours. The Sick attendance marker remains visible without this permission.',
    group: 'visibility',
  },
  {
    key: 'employee_hours.view_holiday_pay',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: View Holiday Pay',
    description: 'Allows this user to see holiday eligibility, decisions, and paid hours.',
    group: 'visibility',
  },
  {
    key: 'employee_hours.manage_holiday_pay',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Manage Holiday Pay (legacy)',
    description: 'Superseded by "Edit Holiday Pay" and "Approve Holiday Pay" below. No route checks this any more; kept only for old stored overrides.',
    group: 'legacy',
  },
  {
    key: 'employee_hours.holiday_pay.edit',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Edit Holiday Pay',
    description: 'Allows editing a draft Holiday Pay review before it is finalized.',
    group: 'action',
  },
  {
    key: 'employee_hours.holiday_pay.approve',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Approve Holiday Pay',
    description: 'Allows finalizing (locking) a Holiday Pay review.',
    group: 'action',
  },
  {
    key: 'employee_hours.view_pto',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: View PTO',
    description: 'Allows this user to see PTO balances and history.',
    group: 'visibility',
  },
  {
    key: 'employee_hours.schedule_pto',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Schedule PTO',
    description: 'Allows scheduling a paid PTO date range for an employee.',
    group: 'action',
  },
  { key: 'employee_hours.special_pay.manage', moduleKey: 'employee_hours', label: 'Employee Hours: Manage special payments', description: 'Create and cancel documented weekly additional payments.', group: 'action' },
  { key: 'employee_hours.payroll.close', moduleKey: 'employee_hours', label: 'Employee Hours: Close payroll period',
    description: 'Closes approved company payroll. Only Operator/CEO may reopen it with a reason.', group: 'action' },
  { key: 'employee_hours.notifications', moduleKey: 'employee_hours', label: 'Employee Hours: Payroll notifications',
    description: 'Receives submissions, period closures and review reminders without permission to approve timesheets.', group: 'visibility' },
  {
    key: 'employee_hours.edit_rate',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Edit hourly rate',
    description: 'Allows setting an employee’s hourly rate. Checked in addition to "Show hourly rates" — you must be able to see a rate to set one.',
    group: 'action',
  },
  {
    key: 'employee_hours.delete_rate_history',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Delete rate history entry',
    description: 'Permanently deletes a row from an employee’s rate history. Off by default for everyone, including Operators — only Developer bypasses it automatically.',
    group: 'action',
  },
  {
    key: 'employee_hours.edit_rate_history',
    moduleKey: 'employee_hours',
    label: 'Employee Hours: Edit rate history entry',
    description: 'Edits an existing row in an employee’s rate history in place (rate, effective date, or note). Off by default for everyone, including Operators — only Developer bypasses it automatically.',
    group: 'action',
  },
  {
    key: 'projects.manage_staffing',
    moduleKey: 'projects',
    label: 'Manage project staffing',
    description: 'Allows assigning or removing supervisors and employees on projects.',
    group: 'action',
  },
  { key: 'projects.crews.manage', moduleKey: 'projects', label: 'Manage work crews', description: 'Create and organize work crews using personnel already on the project. Requires Projects view; staffing and finances stay separate.', group: 'action' },
  {
    key: 'projects.rented.manage',
    moduleKey: 'projects',
    label: 'Manage rented personnel',
    description: 'Creates, edits, groups, archives and restores rented workers in accessible projects. Does not grant company staffing or financial access. Requires Projects view access.',
    group: 'action',
  },
  {
    key: 'projects.rented.delete',
    moduleKey: 'projects',
    label: 'Delete rented workers',
    description: 'Allows deleting rented workers without timesheet history. Also requires Manage rented personnel. Historical records remain protected.',
    group: 'action',
  },
  {
    key: 'projects.workforce.photo',
    moduleKey: 'projects',
    label: 'Workforce Info: Photo',
    description: 'In a project Workforce, shows the photo of the workers on that project (read-only).',
    group: 'visibility',
  },
  {
    key: 'projects.workforce.emergency_contacts',
    moduleKey: 'projects',
    label: 'Workforce Info: Emergency contacts',
    description: 'In a project Workforce, shows the emergency contacts of the workers on that project (read-only).',
    group: 'visibility',
  },
  {
    key: 'projects.workforce.credentials',
    moduleKey: 'projects',
    label: 'Workforce Info: Credentials',
    description: 'In a project Workforce, shows the credentials of the workers on that project and lets the user open their files (read-only).',
    group: 'visibility',
  },
  {
    key: 'projects.workforce.people',
    moduleKey: 'projects',
    label: 'People (foremen): my projects',
    description: 'Shows a People page with the workers on the projects this user works on. Photo, emergency contacts and credentials still need their own Workforce Info permissions.',
    group: 'visibility',
  },
  {
    key: 'projects.workforce.add_existing',
    moduleKey: 'projects',
    label: 'Workforce: Move workers into my projects',
    description: 'Brings an active company worker into a project this account can access. A worker on another project is moved here and stays visible there until Sunday. Does not create people, remove workers or change access.',
    group: 'action',
  },
  {
    key: 'projects.tasks.view',
    moduleKey: 'projects',
    label: 'Tasks: See and do my tasks',
    description: 'Shows the tasks of the projects this user works on (timesheet, daily report, SPA, Toolbox Talk…) in Tasks and on the dashboard.',
    group: 'visibility',
  },
  {
    key: 'projects.tasks.manage',
    moduleKey: 'projects',
    label: 'Tasks: Configure project tasks',
    description: 'Allows adding, editing and deleting the tasks of a project in Project Settings.',
    group: 'action',
  },
  {
    key: 'projects.tasks.no_work_day',
    moduleKey: 'projects',
    label: 'Tasks: Mark "No work today"',
    description: 'Allows marking a day as no work on a project, which closes that day’s tasks without notices.',
    group: 'action',
  },
  {
    key: 'projects.tasks.alerts',
    moduleKey: 'projects',
    label: 'Tasks: Receive missing-task summary',
    description: 'Sends this user one in-app notice each morning listing the project tasks that were not done the day before.',
    group: 'visibility',
  },
  {
    key: 'schedule.edit_structure',
    moduleKey: 'schedule',
    label: 'Edit schedule structure',
    description: 'Allows creating, reordering, and deleting tasks, categories, and dependencies.',
    group: 'action',
  },
  {
    key: 'schedule.update_progress',
    moduleKey: 'schedule',
    label: 'Update schedule progress',
    description: 'Allows updating daily worker counts and task execution progress.',
    group: 'action',
  },
  {
    key: 'schedule.override_locks',
    moduleKey: 'schedule',
    label: 'Override task locks',
    description: 'Allows force-unlocking or re-locking past, started, or completed tasks with a required reason. Without this, locked tasks are read-only.',
    group: 'action',
  },
  {
    key: 'production.zone_soft_restrict',
    moduleKey: 'field_logs',
    label: 'Field Reports: Assigned zones only',
    description: 'Restricts daily production log entries and field reports strictly to assigned zones.',
    group: 'scope',
  },
  {
    key: 'warehouse.purchases_view',
    moduleKey: 'warehouse',
    label: 'Warehouse: View Purchases',
    description: 'Allows this user to see the Purchases tab and read purchase records.',
    group: 'visibility',
  },
  {
    key: 'warehouse.analytics_view',
    moduleKey: 'warehouse',
    label: 'Warehouse: Consumption and spending analysis',
    description: 'View product quantities within accessible projects. Delivery costs require financial visibility; purchases require View Purchases.',
    group: 'visibility',
  },
  {
    key: 'warehouse.purchases_create_draft',
    moduleKey: 'warehouse',
    label: 'Warehouse: Create draft purchases',
    description: 'Allows creating and editing draft purchase records.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_upload_receipt',
    moduleKey: 'warehouse',
    label: 'Warehouse: Upload receipts',
    description: 'Allows uploading receipt photos to a purchase.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_submit',
    moduleKey: 'warehouse',
    label: 'Warehouse: Submit purchases',
    description: 'Allows submitting a draft purchase for review. Off by default — operator must grant.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_link_request',
    moduleKey: 'warehouse',
    label: 'Purchases: Link a receipt to a Request',
    description: 'Reconciles a posted direct-project purchase with a delivery, without adding stock or charging its cost again.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_post',
    moduleKey: 'warehouse',
    label: 'Warehouse: Post (approve) purchases',
    description: 'Allows posting a submitted purchase, which applies inventory and cost effects. Off by default.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_void',
    moduleKey: 'warehouse',
    label: 'Warehouse: Void purchases',
    description: 'Allows voiding a posted purchase (subject to WAVG safety check). Off by default.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_reopen_posted',
    moduleKey: 'warehouse',
    label: 'Warehouse: Reopen posted purchases',
    description: 'Allows safely reversing an untouched posted purchase back to Draft. Requires a reason and is off by default for everyone.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_approve_receipt_exception',
    moduleKey: 'warehouse',
    label: 'Warehouse: Approve receipt exceptions',
    description: 'Allows approving or rejecting receipt exception requests on purchases.',
    group: 'action',
  },
  {
    key: 'warehouse.items_manage_media',
    moduleKey: 'warehouse',
    label: 'Warehouse: Manage item photos & aliases',
    description: 'Allows uploading/deleting item photos, setting the primary photo, and adding or removing "also known as" aliases.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_create',
    moduleKey: 'requests',
    label: 'Warehouse: Create requests',
    description: 'Allows submitting a warehouse request, including "Can\'t find it" (photo-based, unidentified item) requests. Off by default.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_view_own',
    moduleKey: 'requests',
    label: 'Requests: View own',
    description: 'View requests created by this person within their Project Scope.',
    group: 'visibility',
  },
  {
    key: 'warehouse.requests_view_project',
    moduleKey: 'requests',
    label: 'Requests: View project requests',
    description: 'View requests from every requester in permitted projects.',
    group: 'visibility',
  },
  {
    key: 'warehouse.requests_cancel_own',
    moduleKey: 'requests',
    label: 'Requests: Cancel own',
    description: 'Cancel the undelivered portion of requests created by this person.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_manage_queue',
    moduleKey: 'requests',
    label: 'Requests: Manage driver queue',
    description: 'View and organize the fulfillment queue for permitted projects.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_prepare',
    moduleKey: 'requests',
    label: 'Requests: Prepare items',
    description: 'Prepare quantities and reserve QR-tagged units for delivery.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_dispatch',
    moduleKey: 'requests',
    label: 'Requests: Dispatch',
    description: 'Dispatch prepared items and create in-transit inventory movements.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_record_delivery',
    moduleKey: 'requests',
    label: 'Requests: Record delivery',
    description: 'Record delivered quantities and required line-level evidence.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_close_not_supplied',
    moduleKey: 'requests',
    label: 'Requests: Close as not supplied',
    description: 'Close an unfulfilled remainder with a required reason.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_confirm_receipt',
    moduleKey: 'requests',
    label: 'Requests: Confirm receipt',
    description: 'Confirm receipt of delivered items for permitted projects.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_dispute_receipt',
    moduleKey: 'requests',
    label: 'Requests: Dispute receipt',
    description: 'Report a problem with delivered items for permitted projects.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_manual_asset_override',
    moduleKey: 'requests',
    label: 'Requests: Manual QR override',
    description: 'Select a serialized unit manually when its QR cannot be read; requires reason and evidence.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_view_history',
    moduleKey: 'requests',
    label: 'Requests: View full history',
    description: 'View complete operational history for requests in permitted projects.',
    group: 'visibility',
  },
  {
    key: 'warehouse.requests_comment',
    moduleKey: 'requests',
    label: 'Requests: Comment and collaborate',
    description: 'Add operational comments to requests in permitted projects.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_configure',
    moduleKey: 'requests',
    label: 'Requests: Configure workflow',
    description: 'Change company-wide Request workflow settings, including QR-assisted preparation.',
    group: 'action',
  },
  {
    key: 'warehouse.inventory_corrections_create',
    moduleKey: 'requests',
    label: 'Inventory corrections: Report',
    description: 'Report missing, extra, incorrect, damaged, or lost project inventory.',
    group: 'action',
  },
  {
    key: 'warehouse.inventory_corrections_review',
    moduleKey: 'requests',
    label: 'Inventory corrections: Review',
    description: 'Review, verify, approve, or reject project inventory correction requests.',
    group: 'action',
  },
  {
    key: 'warehouse.inventory_corrections_apply',
    moduleKey: 'requests',
    label: 'Inventory corrections: Apply',
    description: 'Apply an approved correction through an audited inventory movement.',
    group: 'action',
  },
  {
    key: 'warehouse.inventory_corrections_reverse',
    moduleKey: 'requests',
    label: 'Inventory corrections: Reverse',
    description: 'Reverse an applied correction with a compensating movement.',
    group: 'action',
  },
  {
    key: 'warehouse.can_checkout',
    moduleKey: 'warehouse',
    label: 'Warehouse: Check out tools/materials',
    description: 'Allows checking a tool or unit out to a project. Included automatically while Warehouse is set to Full Control.',
    group: 'action',
  },
  {
    key: 'warehouse.can_checkin',
    moduleKey: 'warehouse',
    label: 'Warehouse: Check in tools/materials',
    description: 'Allows checking a tool or unit back in from a project. Included automatically while Warehouse is set to Full Control.',
    group: 'action',
  },
  {
    key: 'warehouse.worker_tools_issue',
    moduleKey: 'warehouse',
    label: 'Company tools: Hand out and take back worker tools',
    description: 'Hands numbered company tools (hard hats, harnesses...) to workers on the projects this person works on, and records their return, damage or loss. Can be given to foremen, office or warehouse staff.',
    group: 'action',
  },
  {
    key: 'warehouse.can_transfer',
    moduleKey: 'warehouse',
    label: 'Warehouse: Transfer between locations/projects',
    description: 'Allows moving units directly between locations or projects, and sending/receiving transfers. Included automatically while Warehouse is set to Full Control.',
    group: 'action',
  },
  {
    key: 'warehouse.can_report_condition',
    moduleKey: 'warehouse',
    label: 'Warehouse: Report damaged/lost/maintenance',
    description: 'Allows reporting a unit as damaged, lost, in maintenance, or marking it available again. Included automatically while Warehouse is set to Full Control.',
    group: 'action',
  },
  {
    key: 'warehouse.can_manage_catalog',
    moduleKey: 'warehouse',
    label: 'Warehouse: Manage catalog & inventory',
    description: 'Allows creating, editing, and deleting catalog items and units, adjusting stock, and adding stock lots. A broad permission — included automatically while Warehouse is set to Full Control.',
    group: 'action',
  },
  {
    key: 'warehouse.can_edit_lot_cost',
    moduleKey: 'warehouse',
    label: 'Warehouse: Edit lot cost',
    description: 'Allows editing the recorded cost of a stock lot (a financial figure).',
    group: 'action',
  },
  {
    key: 'warehouse.requests_approve',
    moduleKey: 'requests',
    label: 'Warehouse: Approve material request',
    description: 'Allows approving a pending material request.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_reject',
    moduleKey: 'requests',
    label: 'Warehouse: Reject material request',
    description: 'Allows rejecting a pending material request.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_fulfill',
    moduleKey: 'requests',
    label: 'Warehouse: Fulfill material request',
    description: 'Allows delivering/fulfilling an approved material request, including deducting stock.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_mark_delivering',
    moduleKey: 'requests',
    label: 'Warehouse: Mark request delivering',
    description: 'Allows marking an approved material request as out for delivery.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_link_item',
    moduleKey: 'requests',
    label: 'Warehouse: Link request to item',
    description: 'Allows linking a free-text ("no lo encuentro") request to a real catalog item.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_resolve_catalog',
    moduleKey: 'requests',
    label: 'Requests: Resolve items not in the catalog',
    description: 'Decide what a free-text request line becomes: a new catalog item, an existing item (optionally saving the typed name as an alias), or this order only. Never changes stock.',
    group: 'action',
  },
  {
    key: 'warehouse.requests_delete',
    moduleKey: 'requests',
    label: 'Warehouse: Delete material request',
    description: 'Allows deleting a material request.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_edit_draft',
    moduleKey: 'warehouse',
    label: 'Warehouse: Edit draft purchase',
    description: 'Allows editing a draft purchase before it is submitted.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_delete_draft',
    moduleKey: 'warehouse',
    label: 'Warehouse: Delete draft purchase',
    description: 'Allows deleting a draft purchase before it is submitted.',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_recall',
    moduleKey: 'warehouse',
    label: 'Warehouse: Recall others’ purchase',
    description: 'Allows recalling a submitted purchase back to draft when you are not the original submitter (you can always recall your own).',
    group: 'action',
  },
  {
    key: 'warehouse.purchases_return_for_correction',
    moduleKey: 'warehouse',
    label: 'Warehouse: Return purchase for correction',
    description: 'Allows returning a submitted purchase to draft status for correction.',
    group: 'action',
  },
  {
    key: 'warehouse.can_manage_locations',
    moduleKey: 'warehouse',
    label: 'Warehouse: Manage locations',
    description: 'Allows creating, editing, and archiving Warehouse locations. Included automatically while Warehouse is set to Full Control.',
    group: 'action',
  },
  {
    key: 'documents.upload',
    moduleKey: 'documents',
    label: 'Documents: Upload',
    description: 'Allows creating document records and uploading their original files.',
    group: 'action',
  },
  {
    key: 'documents.manage_versions',
    moduleKey: 'documents',
    label: 'Documents: Manage versions',
    description: 'Allows adding and publishing immutable versions of an existing document.',
    group: 'action',
  },
  {
    key: 'documents.edit_metadata',
    moduleKey: 'documents',
    label: 'Documents: Edit metadata',
    description: 'Allows editing document title, description, category, and tags.',
    group: 'action',
  },
  {
    key: 'documents.archive',
    moduleKey: 'documents',
    label: 'Documents: Archive',
    description: 'Allows archiving and restoring project document records.',
    group: 'action',
  },
  {
    key: 'documents.manage_categories',
    moduleKey: 'documents',
    label: 'Documents: Manage categories',
    description: 'Allows enabling, disabling, and ordering controlled categories by project.',
    group: 'action',
  },
  {
    key: 'drawings.upload',
    moduleKey: 'drawings',
    label: 'Drawings: Upload',
    description: 'Allows uploading individual drawings, multipage sets, and supported model sources.',
    group: 'action',
  },
  {
    key: 'drawings.offline_download',
    moduleKey: 'drawings',
    label: 'Drawings: Download for offline use',
    description: 'Allows storing private, FORXA-managed drawing copies on this device for use without a connection.',
    group: 'action',
  },
  {
    key: 'drawings.classify',
    moduleKey: 'drawings',
    label: 'Drawings: Classify pages',
    description: 'Allows classifying pages from a drawing set into logical sheets and revisions.',
    group: 'action',
  },
  {
    key: 'drawings.publish',
    moduleKey: 'drawings',
    label: 'Drawings: Publish revisions',
    description: 'Allows making a drawing revision current and superseding the prior revision.',
    group: 'action',
  },
  {
    key: 'drawings.manage_sets',
    moduleKey: 'drawings',
    label: 'Drawings: Manage sets',
    description: 'Allows editing, archiving, and completing drawing sets.',
    group: 'action',
  },
  {
    key: 'drawings.manage_annotations',
    moduleKey: 'drawings',
    label: 'Drawings: Manage annotations (legacy)',
    description: 'Legacy permission, no longer enforced by any route or control. Kept so previously granted per-user overrides remain valid. Pin actions are now controlled by the granular Drawings pin permissions below.',
    group: 'action',
  },
  {
    key: 'drawings.review_matches',
    moduleKey: 'drawings',
    label: 'Drawings: Review proposed matches',
    description: 'Allows confirming or correcting a proposed match to an existing logical sheet before publishing a new revision.',
    group: 'action',
  },
  {
    key: 'drawings.pins_create',
    moduleKey: 'drawings',
    label: 'Drawings: Create pins',
    description: 'Allows placing a new pin on a drawing revision.',
    group: 'action',
  },
  {
    key: 'drawings.pins_comment',
    moduleKey: 'drawings',
    label: 'Drawings: Comment on pins',
    description: 'Allows adding messages to an existing pin conversation.',
    group: 'action',
  },
  {
    key: 'drawings.pins_resolve',
    moduleKey: 'drawings',
    label: 'Drawings: Resolve or reopen pins',
    description: 'Allows marking a pin resolved or reopening it.',
    group: 'action',
  },
  {
    key: 'drawings.pins_delete',
    moduleKey: 'drawings',
    label: 'Drawings: Delete pins',
    description: 'Allows removing a pin.',
    group: 'action',
  },
  {
    key: 'drawings.markups_create',
    moduleKey: 'drawings',
    label: 'Drawings: Create markups',
    description: 'Allows drawing markup geometry (clouds, shapes, text, freehand) on a drawing revision.',
    group: 'action',
  },
  {
    key: 'drawings.markups_edit_own',
    moduleKey: 'drawings',
    label: 'Drawings: Edit own markups',
    description: 'Allows editing markups this user created.',
    group: 'action',
  },
  {
    key: 'drawings.markups_edit_others',
    moduleKey: 'drawings',
    label: "Drawings: Edit others' markups",
    description: 'Allows editing markups created by other users.',
    group: 'action',
  },
  {
    key: 'drawings.markups_delete',
    moduleKey: 'drawings',
    label: 'Drawings: Delete markups',
    description: 'Allows removing markups.',
    group: 'action',
  },
  {
    key: 'drawings.annotate_historical',
    moduleKey: 'drawings',
    label: 'Drawings: Annotate historical revisions',
    description: 'Allows creating pins or markups on a revision that is no longer current. Off by default — a deliberate override.',
    group: 'action',
  },
  {
    key: 'rfis.create_draft',
    moduleKey: 'rfis',
    label: 'RFIs: Create drafts',
    description: 'Allows creating and editing unnumbered RFI drafts.',
    group: 'action',
  },
  {
    key: 'rfis.open',
    moduleKey: 'rfis',
    label: 'RFIs: Open drafts',
    description: 'Allows assigning the official project number and moving a draft to Open.',
    group: 'action',
  },
  {
    key: 'rfis.be_assigned',
    moduleKey: 'rfis',
    label: 'RFIs: Eligible assignee',
    description: 'Allows an authorized Admin to appear as an RFI assignee.',
    group: 'visibility',
  },
  {
    key: 'rfis.assign',
    moduleKey: 'rfis',
    label: 'RFIs: Assign',
    description: 'Allows assigning or reassigning an RFI to an eligible Admin.',
    group: 'action',
  },
  {
    key: 'rfis.respond',
    moduleKey: 'rfis',
    label: 'RFIs: Respond',
    description: 'Allows recording the official internal response and attachments.',
    group: 'action',
  },
  {
    key: 'rfis.close_reopen',
    moduleKey: 'rfis',
    label: 'RFIs: Close or reopen',
    description: 'Allows closing answered RFIs and reopening answered or closed RFIs with history.',
    group: 'action',
  },
  {
    key: 'rfis.void',
    moduleKey: 'rfis',
    label: 'RFIs: Void numbered records',
    description: 'Allows voiding an RFI without deleting or reusing its official number.',
    group: 'action',
  },
  {
    key: 'rfis.manage_all_drafts',
    moduleKey: 'rfis',
    label: 'RFIs: Manage others’ drafts',
    description: 'Allows editing or deleting a draft RFI created by someone else. Without this, you can only edit/delete your own drafts.',
    group: 'action',
  },
  {
    key: 'free_reports.view_all',
    moduleKey: 'free_reports',
    label: 'Free Reports: See everyone’s reports',
    description: 'Shows the free reports written by other people on the projects this user works on. Without this, a user only sees their own reports.',
    group: 'visibility',
  },
  {
    key: 'free_reports.reopen',
    moduleKey: 'free_reports',
    label: 'Free Reports: Reopen submitted reports',
    description: 'Allows returning a submitted free report to draft so its author can correct it. Admin accounts only.',
    group: 'action',
  },
];

export const SUB_PERMISSION_DEFAULTS: Record<SubModuleKey, {
  adminDefault: boolean;
  supervisorDefault: boolean;
  operatorDefault: boolean;
  /** No static per-user default — with no explicit override, resolves to `true`
   * when the user's level on the key's parent module is 'manage', else `false`.
   * See hasSubPermission below and its backend mirror, hasRequestSubPermission. */
  inheritFromManage?: boolean;
}> = {
  'estimates.view_all': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'estimates.view_amounts': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'estimates.mark_won': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'estimates.delete': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'dashboard.project_health': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'requests.notifications': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'people.notifications': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'projects.workforce.notifications': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'dashboard.labor': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.schedule': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.reports': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.alerts': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.credentials': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.offboarding': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.rfi_attention': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.warehouse_attention': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.records_attention': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.projects_at_risk': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.schedule_exceptions': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.production_vs_plan': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'dashboard.payroll_exceptions': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'employee_hours.show_rates': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.show_admin_rates': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.show_earnings': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.view_admin_hours': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'timesheets.personal.submit': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'timesheets.personal.approve': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'timesheets.personal.manage_others': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.view_sick_pay': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'employee_hours.view_holiday_pay': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'employee_hours.manage_holiday_pay': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.holiday_pay.edit': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.holiday_pay.approve': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.view_pto': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'employee_hours.schedule_pto': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'employee_hours.special_pay.manage': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.payroll.close': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.notifications': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'employee_hours.edit_rate': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'employee_hours.delete_rate_history': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'employee_hours.edit_rate_history': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'employee_hours.own_project_only': { adminDefault: false, supervisorDefault: true, operatorDefault: false },
  'reports.overview_general': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'reports.tab_production': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'reports.tab_workforce': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'reports.tab_timeline': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'reports.setup_structure': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'reports.setup_targets': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'reports.can_submit': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.assignments.manage': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'people.mode.manage': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'people.employees': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.employee_requests': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.supervisors': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.admins': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'people.manage_module_access': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'people.rented': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.credentials': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.documents': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'people.manage_credential_types': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'field_logs.can_view': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'field_logs.can_submit': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'field_logs.view_all': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'field_logs.create': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'field_logs.edit': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'field_logs.delete': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'field_logs.attachments': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'free_reports.create': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'free_reports.edit': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'free_reports.delete': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'free_reports.attachments': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'field_logs.approve': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'field_logs.reopen': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'free_reports.can_submit': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'free_reports.view_all': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'free_reports.reopen': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'control.can_snapshot': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'control.can_lock_baseline': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'security.bypass_maintenance': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'timesheets.edit_pending': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'timesheets.can_reopen': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.records.create': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.records.edit': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.records.view_others': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.records.view_drafts': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'timesheets.records.special_hours': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'timesheets.records.submit': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.records.approve': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.records.reject': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.records.delete_row': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.records.any_worker': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'timesheets.can_delete': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.tickets.view_all': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.tickets.prepare_documents': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.tickets.request_signature_links': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.tickets.authorize_signatures': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.tickets.create': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.tickets.upload_files': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.tickets.submit': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'timesheets.tickets.review': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'timesheets.tickets.delete': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'projects.progress.view': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'projects.manage_financials': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'projects.financials.view': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'projects.financials.manage': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'prior_period.baseline.edit': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'projects.manage_staffing': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'projects.crews.manage': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'projects.rented.manage': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'projects.rented.delete': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  // Project Workforce → worker Info (read-only, only workers on the project).
  // Opt-in: name and position are already part of Workforce.
  'projects.workforce.photo': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'projects.workforce.emergency_contacts': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'projects.workforce.credentials': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  // People for foremen: the people on their own projects (each detail keeps its own permission above).
  'projects.workforce.people': { adminDefault: false, supervisorDefault: true, operatorDefault: true },
  'projects.workforce.add_existing': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  // Project tasks (timesheet, daily report, SPA, Toolbox Talk…), configured per project.
  'projects.tasks.view': { adminDefault: false, supervisorDefault: true, operatorDefault: true },
  'projects.tasks.manage': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'projects.tasks.no_work_day': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'projects.tasks.alerts': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'schedule.edit_structure': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'schedule.update_progress': { adminDefault: true, supervisorDefault: true, operatorDefault: true },
  'schedule.override_locks': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'production.zone_soft_restrict': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'warehouse.purchases_view': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.analytics_view': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'warehouse.purchases_create_draft': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_upload_receipt': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_submit': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_link_request': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_post': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_void': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_reopen_posted': { adminDefault: false, supervisorDefault: false, operatorDefault: false },
  'warehouse.purchases_approve_receipt_exception': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.items_manage_media': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_create': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_view_own': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_view_project': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_cancel_own': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_manage_queue': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_prepare': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_dispatch': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_record_delivery': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_close_not_supplied': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_confirm_receipt': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_dispute_receipt': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_manual_asset_override': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_view_history': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_comment': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_configure': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.inventory_corrections_create': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_resolve_catalog': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.inventory_corrections_review': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.inventory_corrections_apply': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.inventory_corrections_reverse': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.can_checkout': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.can_checkin': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.worker_tools_issue': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.can_transfer': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.can_report_condition': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.can_manage_catalog': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.can_manage_locations': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.can_edit_lot_cost': { adminDefault: false, supervisorDefault: false, operatorDefault: true, inheritFromManage: true },
  'warehouse.requests_approve': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_reject': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_fulfill': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_mark_delivering': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_link_item': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.requests_delete': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_edit_draft': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_delete_draft': { adminDefault: true, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_recall': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'warehouse.purchases_return_for_correction': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'documents.upload': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'documents.manage_versions': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'documents.edit_metadata': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'documents.archive': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'documents.manage_categories': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.upload': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.offline_download': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.classify': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.publish': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.manage_sets': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.manage_annotations': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.review_matches': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.pins_create': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.pins_comment': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.pins_resolve': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.pins_delete': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.markups_create': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.markups_edit_own': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.markups_edit_others': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.markups_delete': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'drawings.annotate_historical': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.create_draft': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.open': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.be_assigned': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.assign': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.respond': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.close_reopen': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.void': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
  'rfis.manage_all_drafts': { adminDefault: false, supervisorDefault: false, operatorDefault: true },
};

export const FULL_SUB_MODULE_PERMISSIONS = SUB_MODULES.reduce((acc, item) => {
  acc[item.key] = !item.key.endsWith('.own_project_only') && item.key !== 'timesheets.tickets.delete' && item.key !== 'timesheets.personal.submit';
  return acc;
}, {} as Record<SubModuleKey, boolean>);

export function sanitizeSupervisorModulePermissions(raw?: Partial<ModulePermissions> | null): ModulePermissions {
  const source = normalizeWithDefaults(raw, DEFAULT_SUPERVISOR_PERMISSIONS);
  return ADMIN_MODULES.reduce((acc, module) => {
    const cap = SUPERVISOR_MODULE_CAPS[module.key];
    const value = module.key === 'requests' && source.requests === undefined ? source.warehouse : source[module.key];
    acc[module.key] = MODULE_LEVEL_RANK[value] > MODULE_LEVEL_RANK[cap] ? cap : value;
    return acc;
  }, {} as ModulePermissions);
}

export function sanitizeSupervisorSubPermissions(raw?: Partial<SubModulePermissions> | null): SubModulePermissions {
  const source = raw || {};
  const allowed = new Set<SubModuleKey>(SUPERVISOR_ALLOWED_SUB_PERMISSIONS);
  return SUB_MODULES.reduce((acc, item) => {
    if (allowed.has(item.key) && !SUPERVISOR_MONEY_SUB_PERMISSIONS.includes(item.key)) {
      // Missing permissions inherit the same supervisor defaults as the server.
      // An explicit false remains a denial when an account or template is saved.
      acc[item.key] = typeof source[item.key] === 'boolean'
        ? source[item.key]!
        : SUB_PERMISSION_DEFAULTS[item.key].supervisorDefault;
    }
    return acc;
  }, {} as SubModulePermissions);
}


// Modules where even Operators need explicit permission (Developer controls who sees them)
const STRICT_MODULES: AdminModuleKey[] = ['security', 'in_progress'];

function normalizeWithDefaults(
  raw: Partial<ModulePermissions> | null | undefined,
  defaults: ModulePermissions,
): ModulePermissions {
  const source = raw || {};
  return ADMIN_MODULES.reduce((acc, module) => {
    const value = module.key === 'requests' && source.requests === undefined ? source.warehouse : source[module.key];
    if (value === 'none' || value === 'view' || value === 'manage') {
      acc[module.key] = value;
    } else {
      acc[module.key] = defaults[module.key];
    }
    return acc;
  }, {} as ModulePermissions);
}

export function normalizeModulePermissions(raw?: Partial<ModulePermissions> | null): ModulePermissions {
  return normalizeWithDefaults(raw, DEFAULT_ADMIN_PERMISSIONS);
}

export function hasModuleAccess(
  user: User | null | undefined,
  moduleKey: AdminModuleKey,
  requiredLevel: ModulePermissionLevel = 'view',
) {
  if (!user) return false;
  if (Number(user.is_developer || 0) === 1) return true;

  if (user.role === 'admin') {
    // Operators bypass module checks EXCEPT for strict modules
    if (Number(user.is_operator || user.is_owner_admin || 0) === 1 && !STRICT_MODULES.includes(moduleKey)) return true;
    const permissions = normalizeModulePermissions(user.module_permissions);
    const rank: Record<ModulePermissionLevel, number> = { none: 0, view: 1, manage: 2 };
    return rank[permissions[moduleKey]] >= rank[requiredLevel];
  }

  // Supervisor — check their own module_permissions with supervisor defaults
  const permissions = normalizeWithDefaults(user.module_permissions, DEFAULT_SUPERVISOR_PERMISSIONS);
  const rank: Record<ModulePermissionLevel, number> = { none: 0, view: 1, manage: 2 };
  return rank[permissions[moduleKey]] >= rank[requiredLevel];
}

export function canAccessDuringMaintenance(user: User | null | undefined): boolean {
  return Number(user?.is_developer || 0) === 1 || user?.sub_module_permissions?.['security.bypass_maintenance'] === true;
}

export function isGuidedSupervisor(user: User | null | undefined): boolean {
  return user?.role === 'supervisor' && user.guided_settings?.enabled === true;
}

/** Dashboard bookmarks must respect the person's current access and easy mode. */
export function getLoginLandingPath(user: User, returnTo: string | null): string {
  const pathname = returnTo?.split(/[?#]/)[0].replace(/\/+$/, '');
  if (pathname === '/dashboard' && (isGuidedSupervisor(user) || !hasModuleAccess(user, 'dashboard'))) {
    return getDefaultModulePath(user);
  }
  return returnTo ?? getDefaultModulePath(user);
}

export function getDefaultModulePath(user: User | null | undefined) {
  const routeMap: Record<AdminModuleKey, string> = {
    estimates: '/bids',
    dashboard: '/dashboard',
    timesheets: '/timesheets',
    projects: '/projects',
    // Schedule and Reports only exist inside each project now, so people whose
    // first module is one of them start on the project list.
    schedule: '/projects',
    field_logs: '/projects',
    control: '/projects',
    people: '/people',
    reports: '/projects',
    employee_hours: '/employee-hours',
    prior_period: '/projects',
    export: '/export',
    settings: '/settings',
    security: '/security-center',
    in_progress: '/in-progress',
    warehouse: '/warehouse',
    requests: '/requests',
    documents: '/projects',
    drawings: '/projects',
    rfis: '/projects',
    free_reports: '/projects',
  };

  const pathToModuleKey: Record<string, AdminModuleKey> = Object.entries(routeMap).reduce((acc, [key, path]) => {
    // Multiple project-scoped modules share the Projects landing page. Keep the
    // first owner so a saved /projects start page is still checked as Projects.
    if (!acc[path]) acc[path] = key as AdminModuleKey;
    return acc;
  }, {} as Record<string, AdminModuleKey>);

  if (!user) return '/login';
  if (hasModuleAccess(user, 'estimates') && !hasModuleAccess(user, 'dashboard') && !hasModuleAccess(user, 'projects')) return '/estimator';
  // Easy mode opens the checklist when Tasks access is granted.
  if (user.guided_settings?.enabled && hasSubPermission(user, 'projects.tasks.view')) return '/inicio';
  const preferredPath = user.start_page;
  if (preferredPath && preferredPath !== 'default' && pathToModuleKey[preferredPath]
    && !(isGuidedSupervisor(user) && preferredPath === '/dashboard')) {
    const preferredModule = pathToModuleKey[preferredPath];
    if (Number(user.is_developer || 0) === 1) return preferredPath;
    if (user.role === 'admin' && Number(user.is_operator || user.is_owner_admin || 0) === 1 && !STRICT_MODULES.includes(preferredModule)) {
      return preferredPath;
    }
    if (hasModuleAccess(user, preferredModule, 'view')) {
      return preferredPath;
    }
  }

  if (Number(user.is_developer || 0) === 1) return '/dashboard';
  if (user.role === 'admin' && Number(user.is_operator || user.is_owner_admin || 0) === 1) return '/dashboard';
  if (!hasModuleAccess(user,'dashboard','view') && hasModuleAccess(user,'requests','view')
    && ['warehouse.requests_prepare','warehouse.requests_dispatch','warehouse.requests_record_delivery'].some(key=>hasSubPermission(user,key as SubModuleKey))) return '/home';

  const firstAllowed = ADMIN_MODULES.find((module) => !(isGuidedSupervisor(user) && module.key === 'dashboard') && hasModuleAccess(user, module.key, 'view'));
  return firstAllowed ? routeMap[firstAllowed.key] : '/error/forbidden';
}

/**
 * Check a sub-module permission for a user.
 *
 * If the key is explicitly set in the user's sub_module_permissions it takes
 * priority. Otherwise falls back to role-based defaults:
 *   - developers and operators always get true
 *   - regular admins get `adminDefault`  (usually true — they can see everything)
 *   - supervisors get `supervisorDefault` (usually false for sensitive data)
 *
 * Usage:
 *   hasSubPermission(user, 'employee_hours.show_rates')
 *   hasSubPermission(user, 'reports.tab_workforce', { supervisorDefault: true })
 */
export function hasSubPermission(
  user: User | null | undefined,
  key: SubModuleKey,
  options: { supervisorDefault?: boolean; adminDefault?: boolean; operatorDefault?: boolean } = {},
): boolean {
  if (!user) return false;
  // Maintenance is an individual platform exception, independent of Security access.
  if (key === 'security.bypass_maintenance') return canAccessDuringMaintenance(user);
  const definition = SUB_MODULES.find((item) => item.key === key);
  if (definition && !hasModuleAccess(user, definition.moduleKey, 'view')) return false;

  // Developer is the master account. Per-user overrides must never hide or
  // disable one of its features.
  if (Number(user.is_developer || 0) === 1) return true;

  if (user.role === 'supervisor' && SUPERVISOR_MONEY_SUB_PERMISSIONS.includes(key)) return false;

  // Explicit per-user override takes priority
  const sub = user.sub_module_permissions;
  if (sub && key in sub) return Boolean(sub[key]);

  // Compatibility while projects.manage_financials is split into view/manage.
  if (sub && key === 'projects.financials.view') {
    if (sub['projects.financials.manage'] === true) return true;
    if (typeof sub['projects.manage_financials'] === 'boolean') return sub['projects.manage_financials'];
  }
  if (sub && key === 'projects.financials.manage'
      && typeof sub['projects.manage_financials'] === 'boolean') {
    return sub['projects.manage_financials'];
  }
  if (sub && key === 'projects.manage_financials'
      && typeof sub['projects.financials.manage'] === 'boolean') {
    return sub['projects.financials.manage'];
  }

  const defaults = SUB_PERMISSION_DEFAULTS[key] || { supervisorDefault: false, adminDefault: false, operatorDefault: true };
  const {
    supervisorDefault = defaults.supervisorDefault,
    adminDefault = defaults.adminDefault,
    operatorDefault = defaults.operatorDefault,
  } = options;

  // Operators default to full access unless the sub-permission is restrictive.
  if (user.role === 'admin' && Number(user.is_operator || user.is_owner_admin || 0) === 1) return operatorDefault;

  // Keys marked inheritFromManage (e.g. the Warehouse action permissions) have no
  // static per-user default of their own — with no explicit override, they follow
  // whatever the user's actual level on the key's parent module currently is.
  // Computed live, never written, so raising/lowering the module level takes
  // effect immediately with nothing to backfill or go stale.
  if (defaults.inheritFromManage && definition && user.module_permissions?.[definition.moduleKey] === 'manage') {
    return true;
  }

  // Role-based fallback
  return user.role === 'supervisor' ? supervisorDefault : adminDefault;
}
